import PropTypes from 'prop-types';
import { useState } from 'react';
import { NavLink as RouterLink, useLocation } from 'react-router-dom';
// @mui
import { styled } from '@mui/material/styles';
import { Button, Link, List, ListItemButton, Popover, Stack, Typography } from '@mui/material';
// components
import Iconify from '../../../components/iconify';

// ----------------------------------------------------------------------

// A nav item is active for its own route and any nested route below it, so
// "/blog/some-post" still highlights "Blogs".
export function isActivePath(pathname, path) {
  if (!path) {
    return false;
  }

  if (path === '/') {
    return pathname === '/';
  }

  return pathname === path || pathname.startsWith(`${path}/`);
}

const NavItemStyle = styled(Link)(({ theme }) => ({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  marginLeft: theme.spacing(3.5),
  padding: theme.spacing(0.5, 0),
  fontFamily: 'inherit',
  fontSize: 14,
  fontWeight: 600,
  lineHeight: 1.6,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  color: theme.palette.text.primary,
  background: 'none',
  border: 0,
  cursor: 'pointer',
  transition: theme.transitions.create('color'),
  '&::after': {
    content: "''",
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: -4,
    height: 2,
    borderRadius: 1,
    backgroundColor: 'currentColor',
    transform: 'scaleX(0)',
    transformOrigin: 'left center',
    transition: 'transform 0.25s ease',
  },
  '&:hover': { color: theme.palette.primary.main },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 4,
    borderRadius: 4,
  },
  '&:hover::after, &[aria-current="page"]::after': { transform: 'scaleX(1)' },
  '&[aria-current="page"]': { color: theme.palette.primary.main },
}));

const SubItemStyle = styled(ListItemButton)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  color: theme.palette.text.secondary,
  '&:hover': {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  },
}));

// ----------------------------------------------------------------------

DesktopItem.propTypes = {
  item: PropTypes.object.isRequired,
  pathname: PropTypes.string.isRequired,
};

function DesktopItem({ item, pathname }) {
  const { title, path, children } = item;
  const [anchorEl, setAnchorEl] = useState(null);
  const isActive = isActivePath(pathname, path);

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  if (Array.isArray(children) && children.length) {
    const groups = children.filter((group) => Array.isArray(group.items) && group.items.length);

    return (
      <>
        <NavItemStyle
          component="button"
          type="button"
          onClick={handleOpen}
          aria-haspopup="true"
          aria-expanded={Boolean(anchorEl)}
          sx={isActive ? { color: 'primary.main' } : undefined}
        >
          {title}
          <Iconify
            icon={anchorEl ? 'eva:arrow-ios-upward-fill' : 'eva:arrow-ios-downward-fill'}
            sx={{ width: 16, height: 16 }}
          />
        </NavItemStyle>

        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          slotProps={{
            paper: {
              mt: 2,
              p: 1,
              minWidth: 280,
              borderRadius: 2,
              boxShadow: (theme) => theme.customShadows.z24,
            },
          }}
        >
          {groups.map((group) => (
            <List key={group.subheader} disablePadding sx={{ mb: groups.length > 1 ? 1 : 0 }}>
              {group.subheader && (
                <Typography
                  variant="overline"
                  sx={{ display: 'block', px: 1.5, pt: 1, pb: 0.5, color: 'text.disabled' }}
                >
                  {group.subheader}
                </Typography>
              )}

              {group.items.map((subItem) => {
                const subActive = pathname === subItem.path;

                return (
                  <SubItemStyle
                    key={subItem.title}
                    component={RouterLink}
                    to={subItem.path}
                    onClick={handleClose}
                    aria-current={subActive ? 'page' : undefined}
                    sx={subActive ? { color: 'primary.main' } : undefined}
                  >
                    <Typography variant="body2" sx={{ fontWeight: subActive ? 600 : 400 }}>
                      {subItem.title}
                    </Typography>
                  </SubItemStyle>
                );
              })}
            </List>
          ))}
        </Popover>
      </>
    );
  }

  return (
    <NavItemStyle component={RouterLink} to={path} aria-current={isActive ? 'page' : undefined}>
      {title}
    </NavItemStyle>
  );
}

// ----------------------------------------------------------------------

MenuDesktop.propTypes = {
  navConfig: PropTypes.array,
};

export default function MenuDesktop({ navConfig = [] }) {
  const { pathname } = useLocation();

  return (
    <Stack direction="row" sx={{ alignItems: 'center', flexShrink: 0 }}>
      {navConfig.map((item) => (
        <DesktopItem key={item.title} item={item} pathname={pathname} />
      ))}

      <Button
        component={RouterLink}
        to="/courses"
        size="small"
        variant="contained"
        endIcon={<Iconify icon="eva:arrow-forward-outline" sx={{ width: 18, height: 18 }} />}
        sx={{ ml: 3.5, px: 2.25, borderRadius: 1.5, whiteSpace: 'nowrap' }}
      >
        Explore Courses
      </Button>
    </Stack>
  );
}
